<script lang="ts" context="module">
    import { size } from '@floating-ui/core';
    import type { lazy } from '$lib/utils/types.js';
    import { cx } from '$lib/utils/cx';

    export type Choice<TValue> = {
        value: TValue;
        key: string;
        label: string;
    };

    export function createSizeMiddleware(wrapEl: lazy<HTMLElement | null>) {
        return size({
            async apply(args) {
                const el = wrapEl();
                if (el != null && args.platform.getClientRects != null) {
                    const wrapRects = await args.platform.getClientRects(el);
                    const wrapRect = wrapRects.at(0) ?? null;

                    if (wrapRect != null) {
                        el.style.setProperty(
                            '--svelte-select--wrap-left',
                            `${wrapRect.left}px`,
                        );
                        el.style.setProperty(
                            '--svelte-select--wrap-right',
                            `${wrapRect.right}px`,
                        );
                    }

                    el.style.setProperty(
                        '--svelte-select--reference-width',
                        `${args.rects.reference.width}px`,
                    );
                    el.style.setProperty(
                        '--svelte-select--reference-height',
                        `${args.rects.reference.height}px`,
                    );
                    el.style.setProperty(
                        '--svelte-select--available-width',
                        `${args.availableWidth}px`,
                    );
                    el.style.setProperty(
                        '--svelte-select--available-height',
                        `${args.availableHeight}px`,
                    );
                }
            },
        });
    }

    export const wrapSvelteSelectDefaultClass = cx(
        'wrap-svelte-select',
        '[&.svelte-select-xs]:[--font-size:12px]',
        '[&.svelte-select-sm]:[--font-size:14px]',
        '[--svelte-select--list-width:100vw] t:[--svelte-select--list-width:100%]',
        '[--value-container-padding:0px]',
    );
</script>

<script lang="ts" generics="TValues, TRequired extends true|false, TValue">
    import Select from 'svelte-select';
    import { onMount } from 'svelte';
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import Spinner from '$components/Spinner/Spinner.svelte';
    import type { ComputeConfig } from 'svelte-floating-ui';
    import { offset, shift, flip } from '@floating-ui/core';

    type TEffectiveValue = TRequired extends true ? TValue : TValue | null;

    export let required: TRequired;
    export let disableClearable: boolean = false;
    export let form: Form<TValues, string>;
    export let field: Field<TValues, TEffectiveValue>;

    export let placeholder: string = '';

    export let defaultClass: string = wrapSvelteSelectDefaultClass;
    let className: string = '';
    export { className as class };

    export let defaultItemClass: string = 'px-5 py-4';
    export let id: string | null = null;
    export let showChevron = false;

    export let searchable = true;

    export let options: readonly Choice<TValue>[];

    export let itemFilter: ((label: string, filterText: string) => boolean) | null = null;
    export let listAutoWidth: boolean = true;
    export let listOpen: boolean | undefined = undefined;

    let mounted = false;
    onMount(() => {
        mounted = true;
    });

    let _value = form.stores().fieldValue(field);
    let value = $_value;
    let option: Choice<TValue> | null = null;

    let wrapEl: HTMLElement | null = null;
    const floatingConfig: ComputeConfig = {
        strategy: 'absolute',
        placement: 'bottom-start',
        middleware: [offset(5), createSizeMiddleware(() => wrapEl), flip(), shift()],
        autoUpdate: true,
    };

    function onBlur(_event: CustomEvent<unknown>) {
        form.dispatch(form.actions().blur(field));
    }

    function onFocus(_event: CustomEvent<unknown>) {
        form.dispatch(form.actions().focus(field));
    }

    function onChange(event: CustomEvent<Choice<TValue>>) {
        if (event.detail.value !== null || required === false) {
            value = event.detail.value as TEffectiveValue;
            option = event.detail;
            form.dispatch(form.actions().changeValue(value, field));
        }
    }

    function onClear() {
        if (required === false) {
            // Ici, comme required est faux si et seulement si TValue est nullable, il n'y a aucun risque à faire value = null
            value = null as TEffectiveValue;
            option = null;
            form.dispatch(form.actions().changeValue(null as TEffectiveValue, field));
        }
    }

    async function syncV(mounted: boolean, __v: unknown, __option: unknown) {
        if (mounted === false) {
            return;
        }

        if (value == null) {
            option = null;
        } else if (
            value != null &&
            (option == null || option.key !== options.find((o) => o.value == value)?.key)
        ) {
            option = options.find((o) => o.value == value) ?? null;
        }
    }

    $: _value = form.stores().fieldValue(field);
    $: value = $_value;
    $: nbSubmits = form.stores().nbSubmits();
    $: isAlreadyBlur = form.stores().isAlreadyBlur(field);
    $: hasAnyErrors = form.stores().hasFieldAnyErrors(field);
    $: fieldId = form.getFieldId(field);

    $: syncV(mounted, value, option);

    interface $$Slots {
        prepend: Record<string, never>;
        selection: {
            selection: Choice<TValue>;
        };
        'list-prepend': Record<string, never>;
        'list-append': Record<string, never>;
        option: {
            option: Choice<TValue>;
            index: number;
        };
        empty: Record<string, never>;
        'input-hidden': { value: TValue | null };
        required: Record<string, never>;
    }
</script>

<div
    class={cx(defaultClass, className)}
    bind:this={wrapEl}
    class:is-invalid={($nbSubmits > 0 || $isAlreadyBlur) && $hasAnyErrors}
>
    <Select
        class="svelte-select"
        items={options}
        id={id ?? fieldId}
        {listOpen}
        value={option}
        label={'label'}
        itemId={'key'}
        on:blur={onBlur}
        on:focus={onFocus}
        on:change={onChange}
        on:clear={onClear}
        {floatingConfig}
        hasError={$hasAnyErrors && $nbSubmits >= 1 && $isAlreadyBlur}
        {placeholder}
        {showChevron}
        clearable={required === false ? true : !disableClearable}
        {searchable}
        {listAutoWidth}
        itemFilter={itemFilter ?? undefined}
    >
        <slot name="prepend" slot="prepend" />
        <slot name="selection" slot="selection" let:selection {selection}
            >{selection.label ?? ''}</slot
        >
        <svelte:fragment slot="loading-icon">
            <Spinner size="sm" />
        </svelte:fragment>
        <slot name="list-prepend" slot="list-prepend" />
        <slot name="list-append" slot="list-append" />
        <slot name="option" slot="item" let:item let:index {index} option={item}>
            <div class={defaultItemClass}>{item?.label ?? ''}</div>
        </slot>
        <slot name="empty" slot="empty" />
        <slot name="input-hidden" slot="input-hidden" let:value {value} />
        <slot name="required" slot="required" />
    </Select>
</div>
